import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { Activity, ArrowLeft, Building2, Globe, Mail, Phone, ReceiptText, Tag } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { useAdminCustomerDetail, useApplyDiscount, useRemoveDiscount, useProviderLogs, useSetEmailVerified } from "@/hooks/useAdmin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import { formatDate } from "@/lib/utils";

export function AdminCustomerDetailPage() {
  const { id } = useParams();
  const numericId = id ? Number(id) : undefined;
  const { data, isLoading, isError } = useAdminCustomerDetail(numericId);

  return (
    <div className="space-y-5">
      <Link
        to="/admin/customers"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to customers
      </Link>

      {isLoading ? (
        <DetailSkeleton />
      ) : isError || !data ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Couldn't load this customer. They may have been removed.
          </CardContent>
        </Card>
      ) : (
        <>
          <ProfileCard data={data} />
          <SubscriptionsCard subs={data.subscriptions} />
          <DiscountCard accountId={data.id} />
          <DomainsCard domains={data.domains} />
          <ProviderActivityCard accountId={data.id} />
        </>
      )}
    </div>
  );
}

function ProfileCard({ data }: { data: NonNullable<ReturnType<typeof useAdminCustomerDetail>["data"]> }) {
  const { toast } = useToast();
  const setVerified = useSetEmailVerified(data.id);
  const verified = data.email_verified ?? false;

  async function toggleVerified() {
    try {
      await setVerified.mutateAsync(!verified);
      toast({ title: verified ? "Email marked unverified" : "Email marked verified", variant: "success" });
    } catch {
      toast({ title: "Couldn't update verification", variant: "destructive" });
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <CardTitle className="truncate text-xl sm:text-2xl">
            {data.user_full_name || data.user_email || `Account #${data.id}`}
          </CardTitle>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
            <Mail className="h-3.5 w-3.5" />
            {data.user_email ?? "—"}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-wrap gap-2">
            <Badge variant={data.status === "active" ? "success" : "outline"} className="uppercase">
              {data.status}
            </Badge>
            {data.user_role && <Badge variant="outline">{data.user_role}</Badge>}
            <Badge variant={verified ? "success" : "destructive"}>
              {verified ? "Email verified" : "Email unverified"}
            </Badge>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={toggleVerified}
            disabled={setVerified.isPending}
            title="Manually verify (fallback if the email service is down) or revoke verification"
          >
            {setVerified.isPending
              ? "Saving…"
              : verified ? "Mark unverified" : "Mark verified"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-4 border-t border-border pt-5 sm:pt-6 sm:grid-cols-3">
        <Field icon={<Building2 className="h-3.5 w-3.5" />} label="Company" value={data.company_name ?? "—"} />
        <Field icon={<Phone className="h-3.5 w-3.5" />} label="Phone" value={data.phone ?? "—"} />
        <Field label="HostShop ID" value={data.hostshop_customer_id ?? "—"} mono />
        <Field label="Stripe ID" value={data.stripe_customer_id ?? "—"} mono />
        <Field label="Joined" value={formatDate(data.created_at)} />
        <Field label="Updated" value={data.updated_at ? formatDate(data.updated_at) : "—"} />
      </CardContent>
    </Card>
  );
}

function SubscriptionsCard({ subs }: { subs: NonNullable<ReturnType<typeof useAdminCustomerDetail>["data"]>["subscriptions"] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <ReceiptText className="h-5 w-5 text-muted-foreground" />
          Subscriptions
          <span className="text-sm font-normal text-muted-foreground">({subs.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {subs.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">No subscriptions on this account.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Cycle</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Renews</TableHead>
                <TableHead className="text-right">Price</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subs.map(s => (
                <TableRow key={s.id}>
                  <TableCell>
                    <p className="font-medium">{s.plan_name}</p>
                    <p className="text-xs capitalize text-muted-foreground">{s.plan_type}</p>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(s.status)} className="capitalize">{s.status}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{cycleLabel(s.billing_months)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDate(s.started_at)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {s.cancelled_at ? `Cancelled ${formatDate(s.cancelled_at)}` : formatDate(s.renews_at ?? undefined)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {s.currency} {s.price_monthly.toFixed(2)}/mo
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function DiscountCard({ accountId }: { accountId: number }) {
  const { toast } = useToast();
  const apply  = useApplyDiscount(accountId);
  const remove = useRemoveDiscount(accountId);

  const [type, setType]         = React.useState<"percent" | "amount">("percent");
  const [value, setValue]       = React.useState("");
  const [duration, setDuration] = React.useState<"once" | "repeating" | "forever">("forever");
  const [months, setMonths]     = React.useState("3");

  const selectCls =
    "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm " +
    "ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";

  async function onApply() {
    const num = Number(value);
    if (!num || num <= 0) {
      toast({ title: "Enter a discount value", variant: "destructive" });
      return;
    }
    try {
      await apply.mutateAsync({
        type,
        value: num,
        duration,
        duration_in_months: duration === "repeating" ? Number(months) || 1 : undefined
      });
      toast({ title: "Discount applied", variant: "success" });
      setValue("");
    } catch (err) {
      toast({
        title: "Couldn't apply discount",
        description: err instanceof ApiError ? err.message : "Network error.",
        variant: "destructive"
      });
    }
  }

  async function onRemove() {
    try {
      await remove.mutateAsync();
      toast({ title: "Discount removed", variant: "success" });
    } catch (err) {
      toast({
        title: "Couldn't remove discount",
        description: err instanceof ApiError ? err.message : "Network error.",
        variant: "destructive"
      });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Tag className="h-4 w-4 text-primary" />
          Discount
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Apply a concession to this customer's active subscription. Stripe applies it to every
          invoice for the chosen duration.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Type</Label>
            <select className={selectCls} value={type} onChange={(e) => setType(e.target.value as "percent" | "amount")}>
              <option value="percent">Percent off (%)</option>
              <option value="amount">Fixed amount off</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <Label>{type === "percent" ? "Percent (e.g. 20)" : "Amount (e.g. 5.00)"}</Label>
            <Input
              type="number"
              min="0"
              step={type === "percent" ? "1" : "0.01"}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={type === "percent" ? "20" : "5.00"}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Duration</Label>
            <select className={selectCls} value={duration} onChange={(e) => setDuration(e.target.value as "once" | "repeating" | "forever")}>
              <option value="once">Once (first invoice)</option>
              <option value="repeating">For a number of months</option>
              <option value="forever">Lifetime</option>
            </select>
          </div>

          {duration === "repeating" && (
            <div className="space-y-1.5">
              <Label>Months</Label>
              <Input type="number" min="1" step="1" value={months} onChange={(e) => setMonths(e.target.value)} placeholder="3" />
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={onApply} disabled={apply.isPending}>
            {apply.isPending ? "Applying…" : "Apply discount"}
          </Button>
          <Button variant="outline" onClick={onRemove} disabled={remove.isPending}>
            {remove.isPending ? "Removing…" : "Remove discount"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DomainsCard({ domains }: { domains: NonNullable<ReturnType<typeof useAdminCustomerDetail>["data"]>["domains"] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Globe className="h-5 w-5 text-muted-foreground" />
          Domains
          <span className="text-sm font-normal text-muted-foreground">({domains.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {domains.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">No domains on this account.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Domain</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Website</TableHead>
                <TableHead>20i package</TableHead>
                <TableHead>Renews</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {domains.map(d => (
                <TableRow key={d.id}>
                  <TableCell className="font-medium">{d.domain_name}</TableCell>
                  <TableCell><Badge variant="outline">{d.status}</Badge></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{d.website_type}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{d.twentyi_package_id ?? "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDate(d.renewal_date ?? undefined)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function ProviderActivityCard({ accountId }: { accountId: number }) {
  const { data, isLoading } = useProviderLogs(accountId);
  const logs = data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Activity className="h-4 w-4 text-primary" />
          Provider activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : logs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No provider calls recorded yet. Domain, hosting, mailbox and email
            responses will appear here once the customer's services are provisioned.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">When</th>
                  <th className="py-2 pr-3 font-medium">Service</th>
                  <th className="py-2 pr-3 font-medium">Operation</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pr-3 font-medium">Target</th>
                  <th className="py-2 font-medium">Response</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-border/60 align-top">
                    <td className="py-2 pr-3 whitespace-nowrap text-xs text-muted-foreground">{formatDate(l.created_at)}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{l.provider}</td>
                    <td className="py-2 pr-3 whitespace-nowrap font-mono text-xs">{l.operation}</td>
                    <td className="py-2 pr-3"><Badge variant={logStatusVariant(l.status)}>{l.status}</Badge></td>
                    <td className="py-2 pr-3 whitespace-nowrap text-xs">{l.target ?? "—"}</td>
                    <td className="py-2 text-xs text-muted-foreground">{l.message ?? l.code ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function logStatusVariant(status: string): "success" | "destructive" | "outline" {
  const s = (status ?? "").toLowerCase();
  if (s === "success") return "success";
  if (s === "error") return "destructive";
  return "outline"; // pending
}

function Field({ icon, label, value, mono }: {
  icon?: React.ReactNode; label: string; value: string; mono?: boolean;
}) {
  return (
    <div>
      <p className="flex items-center gap-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {icon}{label}
      </p>
      <p className={`mt-0.5 truncate text-sm font-medium text-foreground ${mono ? "font-mono text-xs" : ""}`}>
        {value}
      </p>
    </div>
  );
}

function statusVariant(status: string): "success" | "destructive" | "outline" {
  const s = status.toLowerCase();
  if (s === "active" || s === "trial") return "success";
  if (s === "cancelled" || s === "expired" || s === "suspended" || s === "pastdue") return "destructive";
  return "outline";
}

function cycleLabel(m: number) { return m === 12 ? "Annual" : m === 3 ? "Quarterly" : "Monthly"; }

function DetailSkeleton() {
  return (
    <div className="space-y-5">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i}>
          <CardContent className="space-y-3 py-6">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
